/** Выбор эмодзи, упоминаний, времени - вставляются в текстовые поля. */
import { h, icon } from '../dom.js';
import { app } from '../ctx.js';
import { mdCtx } from '../preview/render.js';
import { TIMESTAMP_STYLES, discordTimestamp } from '../preview/time.js';
import { popover } from './overlay.js';

const UNICODE_EMOJI =
  '😀 😁 😂 🤣 😊 😍 😎 🤩 🥳 😇 🙂 😉 😌 😴 🤔 🤨 😐 🙄 😏 😬 😮 😱 😭 😡 🤯 🥺 🤗 🤝 👍 👎 👏 🙌 🙏 💪 👀 👋 ✌️ 🤞 👌 👉 👈 ☝️ 👇 ✋ ❤️ 🧡 💛 💚 💙 💜 🖤 🤍 💔 💖 ✨ ⭐ 🌟 💫 🔥 💥 ⚡ 🎉 🎊 🎁 🏆 🥇 🥈 🥉 🎯 🎮 🎲 🎵 🎶 📢 📣 🔔 🔕 📌 📍 📎 🔗 📝 📄 📋 📅 ⏰ ⌛ ⏳ 🔒 🔓 🔑 🛡️ ⚔️ 🧭 🚀 🛸 🌍 🌙 ☀️ ⛅ 🌈 ❄️ 🌊 🍀 🌸 🌹 🍕 🍔 🍩 ☕ 🍺 💎 💰 💳 🛒 📦 ✅ ☑️ ✔️ ❌ ❎ ⚠️ ⛔ 🚫 ❗ ❓ 💬 💭 🗨️ 👑 🤖 👾 💡 🔧 🛠️ ⚙️ 🧩 📊 📈 📉 🆕 🆓 🆙 🔴 🟠 🟡 🟢 🔵 🟣 ⚫ ⚪ ▶️ ⏸️ ⏹️ 🔄 ➡️ ⬅️ ⬆️ ⬇️ 1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣'.split(
    ' ',
  );

export function emojiPicker(anchor: HTMLElement, onPick: (value: string) => void) {
  const search = h('input', { class: 'input input-sm', placeholder: 'Поиск эмодзи сервера...', attrs: { 'aria-label': 'Поиск эмодзи' } });
  const guildGrid = h('div', { class: 'emoji-grid' });
  const uniGrid = h('div', { class: 'emoji-grid' });
  const render = () => {
    const q = search.value.trim().toLowerCase();
    guildGrid.replaceChildren(
      ...(app.guild?.emojis ?? [])
        .filter((e) => !q || e.name.toLowerCase().includes(q))
        .slice(0, 120)
        .map((e) =>
          h(
            'button',
            {
              class: 'emoji-cell',
              type: 'button',
              title: `:${e.name}:`,
              onclick: () => {
                onPick(`<${e.animated ? 'a' : ''}:${e.name}:${e.id}>`);
                p.close();
              },
            },
            h('img', { src: mdCtx.emojiUrl(e.id, e.animated), alt: e.name, loading: 'lazy' }),
          ),
        ),
    );
    if (!guildGrid.childElementCount) guildGrid.append(h('div', { class: 'muted small' }, app.guild?.emojis.length ? 'Ничего не найдено' : 'На сервере нет своих эмодзи'));
  };
  uniGrid.append(
    ...UNICODE_EMOJI.map((e) =>
      h(
        'button',
        {
          class: 'emoji-cell is-unicode',
          type: 'button',
          onclick: () => {
            onPick(e);
            p.close();
          },
        },
        e,
      ),
    ),
  );
  search.addEventListener('input', render);
  render();
  const p = popover(
    anchor,
    h('div', { class: 'picker' }, search, h('div', { class: 'picker-section' }, 'Эмодзи сервера'), guildGrid, h('div', { class: 'picker-section' }, 'Стандартные'), uniGrid),
    { width: 340 },
  );
  setTimeout(() => search.focus(), 30);
}

export function mentionPicker(anchor: HTMLElement, onPick: (value: string) => void) {
  const search = h('input', { class: 'input input-sm', placeholder: 'Роль, канал или ID пользователя', attrs: { 'aria-label': 'Поиск' } });
  const list = h('div', { class: 'picker-list' });
  const pick = (v: string) => {
    onPick(v);
    p.close();
  };
  const render = () => {
    const q = search.value.trim().toLowerCase();
    const items: HTMLElement[] = [];
    if (/^\d{15,25}$/.test(q)) {
      items.push(h('button', { class: 'picker-item', type: 'button', onclick: () => pick(`<@${q}>`) }, icon('user', 16), `Пользователь ${q}`));
    }
    for (const special of ['everyone', 'here']) {
      if (!q || special.includes(q)) items.push(h('button', { class: 'picker-item', type: 'button', onclick: () => pick(`@${special}`) }, icon('at', 16), `@${special}`));
    }
    const roles = (app.guild?.roles ?? []).filter((r) => !r.everyone && (!q || r.name.toLowerCase().includes(q))).slice(0, 40);
    if (roles.length) items.push(h('div', { class: 'picker-section' }, 'Роли'));
    for (const r of roles) {
      items.push(
        h(
          'button',
          { class: 'picker-item', type: 'button', onclick: () => pick(`<@&${r.id}>`) },
          h('span', { class: 'role-dot', style: { background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : '#99aab5' } }),
          r.name,
        ),
      );
    }
    const chans = [...(app.guild?.channels ?? []).filter((c) => c.type !== 4), ...(app.guild?.threads ?? [])]
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .slice(0, 40);
    if (chans.length) items.push(h('div', { class: 'picker-section' }, 'Каналы'));
    for (const c of chans) items.push(h('button', { class: 'picker-item', type: 'button', onclick: () => pick(`<#${c.id}>`) }, icon('hash', 16), c.name));
    if (!q || 'каналы и роли'.includes(q)) {
      items.push(h('div', { class: 'picker-section' }, 'Навигация сервера'));
      items.push(h('button', { class: 'picker-item', type: 'button', onclick: () => pick('<id:customize>') }, icon('hash', 16), 'Каналы и роли'));
      items.push(h('button', { class: 'picker-item', type: 'button', onclick: () => pick('<id:browse>') }, icon('hash', 16), 'Обзор каналов'));
    }
    list.replaceChildren(...items);
    if (!items.length) list.append(h('div', { class: 'muted small pad' }, 'Ничего не найдено'));
  };
  search.addEventListener('input', render);
  render();
  const p = popover(anchor, h('div', { class: 'picker' }, search, list), { width: 300 });
  setTimeout(() => search.focus(), 30);
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function timestampPicker(anchor: HTMLElement, onPick: (value: string) => void) {
  const input = h('input', { class: 'input input-sm', type: 'datetime-local', value: toLocalInput(new Date()), attrs: { 'aria-label': 'Дата и время' } });
  const list = h('div', { class: 'picker-list' });
  const render = () => {
    const unix = Math.floor(new Date(input.value).getTime() / 1000);
    list.replaceChildren(
      ...TIMESTAMP_STYLES.map((s) =>
        h(
          'button',
          {
            class: 'picker-item ts-item',
            type: 'button',
            onclick: () => {
              onPick(`<t:${unix}:${s.style}>`);
              p.close();
            },
          },
          h('span', { class: 'ts-preview' }, Number.isNaN(unix) ? '-' : discordTimestamp(unix, s.style)),
          h('span', { class: 'muted small' }, s.label),
        ),
      ),
    );
  };
  input.addEventListener('input', render);
  render();
  const p = popover(anchor, h('div', { class: 'picker' }, h('div', { class: 'picker-section' }, 'Время показывается каждому в его часовом поясе'), input, list), { width: 320 });
}

/** Вставка/обёртка выделенного текста в поле ввода. */
export function insertText(el: HTMLInputElement | HTMLTextAreaElement, before: string, after = '', placeholder = '') {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const selected = el.value.slice(start, end) || placeholder;
  el.focus();
  el.setRangeText(before + selected + after, start, end, 'end');
  if (!el.value.slice(start, end).length && placeholder) {
    el.setSelectionRange(start + before.length, start + before.length + selected.length);
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Префикс в начало каждой выделенной строки (цитаты, списки, заголовки). */
export function prefixLines(el: HTMLTextAreaElement | HTMLInputElement, prefix: string) {
  const v = el.value;
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;
  const lineStart = v.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = v.indexOf('\n', end) === -1 ? v.length : v.indexOf('\n', end);
  const block = v.slice(lineStart, lineEnd);
  const all = block.split('\n');
  const already = all.every((l) => l.startsWith(prefix));
  const next = all.map((l) => (already ? l.slice(prefix.length) : prefix + l)).join('\n');
  el.focus();
  el.setRangeText(next, lineStart, lineEnd, 'select');
  el.dispatchEvent(new Event('input', { bubbles: true }));
}
